
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar, Clock, Star, Play } from 'lucide-react';
import GameCard from '@/components/game/GameCard';
import { supabase } from '@/integrations/supabase/client';
import { SEO } from '@/components/SEO';
import { Game } from '@/types/game';

interface DatabaseGame {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  game_url: string;
  category: string;
  play_count: number;
  rating: number;
  created_at: string;
  updated_at: string;
  tags: string[];
  featured: boolean;
  trending: boolean;
  hidden: boolean;
  is_new: boolean;
  seo_title?: string;
  seo_description?: string;
  feed_url?: string;
  embed_type: 'iframe' | 'html5';
  game_file?: string;
  fake_play_count?: number;
}

const NewGamesPage = () => {
  const { data: games = [], isLoading, error } = useQuery({
    queryKey: ['new-games'],
    queryFn: async (): Promise<DatabaseGame[]> => {
      const { data, error } = await supabase
        .from('games')
        .select('*')
        .eq('hidden', false)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data as DatabaseGame[]) || [];
    },
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getDaysAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  // Convert database game to GameCard expected format
  const convertToGameCardFormat = (dbGame: DatabaseGame): Game => ({
    id: dbGame.id,
    title: dbGame.title,
    description: dbGame.description || '',
    thumbnail: dbGame.thumbnail || '/placeholder.svg',
    category: dbGame.category,
    tags: dbGame.tags || [],
    featured: dbGame.featured || false,
    trending: dbGame.trending || false,
    isNew: dbGame.is_new || false,
    hidden: dbGame.hidden || false,
    playCount: dbGame.play_count || 0,
    fakePlayCount: dbGame.fake_play_count,
    rating: Number(dbGame.rating) || 0,
    createdAt: dbGame.created_at,
    updatedAt: dbGame.updated_at,
    seoTitle: dbGame.seo_title,
    seoDescription: dbGame.seo_description,
    embedType: 'iframe',
    gameUrl: dbGame.game_url || '',
  });

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Error Loading Games</h2>
          <p className="text-gray-600">Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        pageType="page"
        title="New Games - Latest Online Games"
        description="Discover the newest online games added to our collection. Play the latest browser games for free!"
        keywords="new games, latest games, newest online games, browser games"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="flex items-center justify-center mb-4">
              <Calendar className="w-8 h-8 text-blue-600 mr-3" />
              <h1 className="text-5xl font-bold text-gray-900">
                New <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Games</span>
              </h1>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Fresh arrivals! Check out the latest games added to our collection. Be the first to discover and play new exciting games.
            </p>
          </div>

          {/* Stats Bar */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div className="flex items-center justify-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                  <Play className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{games.length}</p>
                  <p className="text-gray-600">New Games</p>
                </div>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">Daily</p>
                  <p className="text-gray-600">Updates</p>
                </div>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                  <Star className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">Fresh</p>
                  <p className="text-gray-600">Content</p>
                </div>
              </div>
            </div>
          </div>

          {/* Loading State */}
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(12)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl shadow-lg p-4 animate-pulse">
                  <div className="w-full h-48 bg-gray-200 rounded-lg mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded mb-4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : games.length === 0 ? (
            <div className="text-center py-16">
              <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No New Games Yet</h3>
              <p className="text-gray-500">New games will appear here when they're added.</p>
            </div>
          ) : (
            /* Games Grid */
            <div className="space-y-8">
              {/* Recent Games (Last 7 days) */}
              {games.filter(game => {
                const daysAgo = Math.floor((Date.now() - new Date(game.created_at).getTime()) / (1000 * 60 * 60 * 24));
                return daysAgo <= 7;
              }).length > 0 && (
                <div>
                  <div className="flex items-center mb-6">
                    <Clock className="w-5 h-5 text-green-600 mr-2" />
                    <h2 className="text-2xl font-bold text-gray-900">This Week</h2>
                    <div className="ml-3 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                      Fresh
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {games
                      .filter(game => {
                        const daysAgo = Math.floor((Date.now() - new Date(game.created_at).getTime()) / (1000 * 60 * 60 * 24));
                        return daysAgo <= 7;
                      })
                      .map((game: DatabaseGame) => (
                        <div key={game.id} className="relative">
                          <GameCard game={convertToGameCardFormat(game)} />
                          <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                            {getDaysAgo(game.created_at)}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Older Games */}
              <div>
                <div className="flex items-center mb-6">
                  <Calendar className="w-5 h-5 text-blue-600 mr-2" />
                  <h2 className="text-2xl font-bold text-gray-900">Recent Additions</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {games
                    .filter(game => {
                      const daysAgo = Math.floor((Date.now() - new Date(game.created_at).getTime()) / (1000 * 60 * 60 * 24));
                      return daysAgo > 7;
                    })
                    .map((game: DatabaseGame) => (
                      <div key={game.id} className="relative">
                        <GameCard game={convertToGameCardFormat(game)} />
                        <div className="absolute top-2 right-2 bg-blue-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                          {formatDate(game.created_at)}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NewGamesPage;
